import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from './auth-code-flow.config';
import { SymphonyAuthService } from './symphony-auth.service';
import { DashboardService } from '../services/dashboard/dashboard.service';
import { AuthPromptService } from './auth-prompt.service';
import { AuthModeService } from './auth-mode.service';
import { getPathFromBrowser, isErrorPath } from './browser-path.util';
import { ADK_LOADER, WINDOW } from '../app.tokens';

export interface AuthStartupHandlers {
  /**
   * Called once authentication has succeeded and the application should
   * proceed with its normal startup (e.g. dashboard + main UI load).
   */
  onAppReady: () => void;

  /**
   * Optional hook to update UI state while a login redirect is in progress.
   */
  setRedirectingToLogin?: (redirecting: boolean) => void;

  /**
   * Optional hook to signal that the ADK / auth preconditions are satisfied.
   * Historically this has been used to gate rendering of the main UI.
   */
  setAdkReady?: (ready: boolean) => void;
}

/**
 * Central orchestrator for authentication and initial app startup.
 *
 * Handles:
 * - CoT vs IdP branching (via AuthModeService)
 * - Route flag setup (logout / error)
 * - IdP discovery + token checks
 * - CoT ADK startup and JWT validation
 * - Calling DashboardService.onLoad() before handing control to the app
 */
@Injectable({ providedIn: 'root' })
export class AuthOrchestratorService {
  private isLogoutRoute = false;
  private isErrorRoute = false;

  constructor(
    private readonly router: Router,
    private readonly oauthService: OAuthService,
    private readonly symphonyAuthService: SymphonyAuthService,
    private readonly dashboardService: DashboardService,
    private readonly authPromptService: AuthPromptService,
    private readonly authModeService: AuthModeService,
    @Optional() @Inject(ADK_LOADER) private readonly adkLoader?: () => Promise<{ default?: { start: (config: any) => Promise<void> } }>,
    @Optional() @Inject(WINDOW) private readonly winRef?: Window & { location?: { search?: string } }
  ) {}

  /**
   * Entry point used by AppComponent to kick off auth + app startup for
   * the current route.
   */
  bootstrapAppForCurrentRoute(handlers: AuthStartupHandlers): void {
    this.runPostAuthSetup();

    const path = this.router.url.split('?')[0];
    const locationRef = (this.winRef !== undefined ? this.winRef?.location : (typeof globalThis !== 'undefined' && (globalThis as any).location)) || null;
    const hasCallbackCode = typeof locationRef?.search === 'string' && locationRef.search.includes('code=');
    const pathFromBrowser = getPathFromBrowser();
    if (path === '/error' || isErrorPath(pathFromBrowser)) {
      return;
    }
    const isSilentRefresh = typeof locationRef?.search === 'string' && locationRef.search.includes('silent-refresh=true');
    if (isSilentRefresh) {
      return;
    }
    const isHomepage = path === '' || path === '/';
    const getLoginUserInfoFailed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('getLoginUserInfoFailed') === 'true';
    if (getLoginUserInfoFailed && !hasCallbackCode && !isHomepage) {
      return;
    }
    if (isHomepage && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('getLoginUserInfoFailed');
    }
    if (this.authModeService.isIdp()) {
      this.handleIdpStartup(handlers);
      return;
    }

    if (this.authModeService.isCot()) {
      this.handleCotStartup(handlers);
    }
  }

  private handleIdpStartup(handlers: AuthStartupHandlers): void {
    const pathForErrorCheck = (this.winRef?.location as { pathname?: string } | undefined)?.pathname ?? getPathFromBrowser();
    if (isErrorPath(pathForErrorCheck)) {
      return;
    }
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        if (this.oauthService.hasValidAccessToken()) {
          const locationRef = (this.winRef !== undefined ? this.winRef?.location : (globalThis as any).location);
          const hasCallbackCode = typeof locationRef?.search === 'string' && locationRef.search.includes('code=');
          if (typeof locationRef?.search === 'string' && locationRef.search.includes('silent-refresh=true')) {
            return;
          }
          const getLoginUserInfoFailed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('getLoginUserInfoFailed') === 'true';
          if (getLoginUserInfoFailed && !hasCallbackCode) {
            return;
          }
          if (hasCallbackCode && typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('getLoginUserInfoFailed');
          }
          this.oauthService.setupAutomaticSilentRefresh();
          handlers.setAdkReady?.(true);
          this.runAppStartup(handlers);
        } else {
          const loc = this.winRef !== undefined ? this.winRef?.location : (globalThis as any).location;
          const hasCode = typeof loc?.search === 'string' && (loc as { search?: string }).search?.includes('code=');
          if (!hasCode && !this.isOnErrorRoute()) {
            handlers.setRedirectingToLogin?.(true);
            this.authPromptService.promptLogin();
          }
        }
      })
      .catch((err) => {
        console.error('[AuthOrchestrator] loadDiscoveryDocumentAndTryLogin failed', err);
        const loc = this.winRef !== undefined ? this.winRef?.location : (globalThis as any).location;
        const hasCode = typeof (loc as { search?: string })?.search === 'string' && (loc as { search?: string }).search?.includes('code=');
        if (!hasCode && !this.isOnErrorRoute()) {
          handlers.setRedirectingToLogin?.(true);
          this.authPromptService.promptLogin();
        }
      });
  }

  private handleCotStartup(handlers: AuthStartupHandlers): void {
    const win = this.winRef !== undefined ? this.winRef : (globalThis as any).window;
    const hasWindow = win != null;
    if (!hasWindow) {
      handlers.setAdkReady?.(true);
      this.runAppStartup(handlers);
      return;
    }
    const loadAdk = this.adkLoader ?? (() => import('@symphony-ui/adk'));
    loadAdk().then((adkModule) => this.startSymphonyAdk(adkModule, handlers));
  }

  private runAppStartup(handlers: AuthStartupHandlers): void {
    this.dashboardService.onLoad()
      .then(() => {
        const currentPath = this.router.url.split('?')[0];
        if (currentPath !== '/error' && currentPath !== '/logout') {
          handlers.onAppReady();
        }
      })
      .catch((err) => {
        console.error('[AuthOrchestrator] dashboardService.onLoad rejected', err);
        const currentHeading = this.dashboardService.errorMessageHeading.getValue();
        const currentMessage = this.dashboardService.errorMessage.getValue();
        if (!currentHeading || !currentMessage) {
          this.dashboardService.errorMessageHeading.next('Service Unavailable');
          this.dashboardService.errorMessage.next("We're currently experiencing some technical difficulties. Please try again later. If the issue persists, contact support at DC_QwynSupport.");
        }
      });
  }

  private runPostAuthSetup(): void {
    const win = this.winRef !== undefined ? this.winRef : (globalThis as any).window;
    if (win?.location?.search?.includes('silent-refresh=true')) {
      return;
    }
    const setRouteFlags = (url: string) => {
      const path = url.split('?')[0];
      this.isLogoutRoute = path === '/logout';
      this.isErrorRoute = path === '/error';
    };
    setRouteFlags(this.router.url);
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        setRouteFlags(event.urlAfterRedirects);
      }
    });
  }

  /**
   * Expose route flags for components/templates that need to adjust layout
   * based on logout/error routes.
   */
  isLogoutRouteActive(): boolean {
    return this.isLogoutRoute;
  }

  isErrorRouteActive(): boolean {
    return this.isErrorRoute;
  }

  private isOnErrorRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return path === '/error' || isErrorPath(getPathFromBrowser());
  }

  private startSymphonyAdk(adkModule: any, handlers: AuthStartupHandlers): void {
    const ADK = adkModule?.default ?? adkModule;
    const config = this.symphonyAuthService.getConfig();
    ADK.start(config).then(() => {
      handlers.setAdkReady?.(true);
      this.runAppStartup(handlers);
    }).catch((err: unknown) => {
      console.error('[AuthOrchestrator] ADK.start rejected', err);
    });
  }
}
